import { MutableState } from 'src/replicant/State';
import {
  magicLampsGetContinueCost,
  magicLampsGetCurrentPrizes,
  magicLampsGetEntryCost,
  magicLampsGetGrandPrize,
  magicLampsGetOngoingEvent,
  magicLampsGetRewardSet,
  magicLampsIsCompleted,
} from './magiclamps.getters';
import * as Match2 from '..';
import * as Inventory from 'src/replicant/inventory';
import { CurrencyReplicantAPI } from 'src/replicant/inventory/inventory.utils';

/**
 * Initialize magic lamps state and return it.
 */
function magicLampsGetState(state: MutableState, now: number) {
  const currentEvent = magicLampsGetOngoingEvent(now);
  const eventId = currentEvent?.id ?? '';
  // Reset the state if the event id has changed
  if (!state.match2.magicLamps || state.match2.magicLamps.eventId !== eventId) {
    state.match2.magicLamps = {
      eventId,
      lastViewedAt: 0,
      firstPlayedAt: 0,
      lastPlayedAt: 0,
      numPlays: 0,
      round: 1,
      continuesUsed: 0,
      rewards: {},
      trap: false,
      playing: false,
    };
  }
  return state.match2.magicLamps;
}

/**
 * Add rewards to the current pool of accumulated rewards.
 */
function magicLampsAddRewards(
  state: MutableState,
  rewardType: string,
  rewardValue: number,
) {
  // Can't add rewards if not played yet
  if (!state.match2.magicLamps) return;

  // Add new reward entry if it doesn't exist
  if (!state.match2.magicLamps.rewards[rewardType]) {
    state.match2.magicLamps.rewards[rewardType] = 0;
  }

  // Increment reward value
  state.match2.magicLamps.rewards[rewardType] += rewardValue;
}

/**
 * Update last viewed timestamp for magic lamps popup.
 */
export function magicLampsView(state: MutableState, now: number): void {
  const magicLamps = magicLampsGetState(state, now);
  magicLamps.lastViewedAt = now;
}

/**
 * Start magic lamps gameplay
 */
export function magicLampsPlay(
  state: MutableState,
  api: CurrencyReplicantAPI,
): boolean {
  const now = api.date.now();
  const magicLamps = magicLampsGetState(state, now);
  // Reset to first round
  magicLamps.round = 1;
  // Reset continues used
  magicLamps.continuesUsed = 0;
  // Reset continuing to false
  magicLamps.continuing = false;
  // Clear accumulated rewards
  magicLamps.rewards = {};
  // Clear trap
  magicLamps.trap = false;
  // Set playing to true
  magicLamps.playing = true;

  const currentGems = Inventory.getters.getGems(state);
  const entryCost = magicLampsGetEntryCost(state);
  // Can't play if user doesn't have enough gems to pay for it
  if (currentGems < entryCost) return false;

  // Pay for the start the game
  Inventory.modifiers.consumeGems(
    state,
    entryCost,
    {
      feature: 'magic_lamps',
      $subFeature: 'start',
    },
    api,
  );

  if (!magicLamps.firstPlayedAt) {
    // Set first played timestamp
    magicLamps.firstPlayedAt = now;
  }

  // Update last played timestamp
  magicLamps.lastPlayedAt = now;
  // Increase number of plays count
  magicLamps.numPlays += 1;

  // All good, user can play
  return true;
}

/**
 * Play the next round of the magic lamps event, returning the results.
 */
export function magicLampsPlayRound(state: MutableState, now: number) {
  const magicLamps = magicLampsGetState(state, now);

  // User can only play if is not passed the last stage
  if (magicLampsIsCompleted(state)) return null;

  magicLamps.lastPlayedAt = now;

  const prizes = magicLampsGetCurrentPrizes(state);

  if (!prizes) return null;

  const round = magicLamps.round;
  const selected = prizes[0]; // The first prize is always the selected one
  const trap = selected.type === 'trap';

  // Set trap to true if the selected prize is a trap
  if (trap) {
    magicLamps.trap = true;
  }
  // Accumulate rewards from non-trap outcomes.
  else {
    magicLampsAddRewards(state, selected.type, selected.value);
    magicLamps.round += 1;
  }

  // Reset continuing to false
  magicLamps.continuing = false;

  // Check if user has completed the event
  const completed = magicLampsIsCompleted(state);

  if (completed) {
    // Add the grand prize to the reward pool if the event is completed
    magicLampsAddRewards(state, 'grandPrize', magicLampsGetGrandPrize());
  }

  return { prizes, trap, completed, round };
}

/**
 * Pay to continue playing the magic lamps event, in case the user got a trap.
 */
export function magicLampsContinue(
  state: MutableState,
  api: CurrencyReplicantAPI,
): boolean {
  // Can't continue if we haven't played yet
  if (!state.match2.magicLamps) return false;

  const currentGems = Inventory.getters.getGems(state);
  const continueCost = magicLampsGetContinueCost(state);

  // Can't continue if we don't have enough gems to pay for it
  if (currentGems < continueCost) return false;

  // Pay for the continue
  Inventory.modifiers.consumeGems(
    state,
    continueCost,
    {
      feature: 'magic_lamps',
      $subFeature: 'continue',
    },
    api,
  );

  // Increment the number of continues used
  state.match2.magicLamps.continuesUsed += 1;

  // Set continuing to true, so the next play will be safe from traps
  state.match2.magicLamps.continuing = true;

  // Reset trap to false
  state.match2.magicLamps.trap = false;

  // All good, user can continue
  return true;
}

/**
 * Collect all rewards accumulated by playing the magic lamps event.
 */
export function magicLampsCollectRewards(
  state: MutableState,
  subFeature: 'complete' | 'exit',
  api: CurrencyReplicantAPI,
): boolean {
  if (!state.match2.magicLamps) return false;

  const rewardSet = magicLampsGetRewardSet(state);

  const canCollect = rewardSet.length > 0 && !state.match2.magicLamps.trap;

  // Only grant rewards if there is any to collect and user didn't fall into a trap.
  if (canCollect) {
    Match2.modifiers.grantMatch2Rewards(
      state,
      rewardSet,
      {
        feature: 'magic_lamps',
        $subFeature: subFeature,
      },
      api,
    );
  }

  // Clear the reward pool.
  state.match2.magicLamps.rewards = {};
  state.match2.magicLamps.trap = false;
  state.match2.magicLamps.playing = false;

  return canCollect;
}
